import * as pdfjsLib from 'pdfjs-dist';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import Button, { ButtonType } from '../Button/Button';
import { configurePdfjsWorker } from './workerSrc';

const THUMBNAIL_SCALE = 0.3;
const PREVIEW_SCALE = 0.6;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.5rem 2rem 2rem;
  color: var(--text);
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.1rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
  max-height: 50vh;
  overflow-y: auto;
`;

const ThumbnailWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  border: 2px solid var(--border-light);
  border-radius: 4px;
  padding: 8px;

  &:hover {
    border-color: var(--active-blue);
  }
`;

const ThumbnailImage = styled.img`
  max-width: 100%;
  display: block;
`;

const ThumbnailPlaceholder = styled.div`
  width: 100%;
  aspect-ratio: 0.707;
  background: var(--border-light);
  border-radius: 2px;
  animation: pulse 1.2s ease-in-out infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.4;
    }
  }
`;

const PageLabel = styled.span`
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const PreviewWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
  overflow: visible;
`;

const PreviewImage = styled.img<{ $visualRotation: number }>`
  display: block;
  max-width: min(100%, 50vh);
  max-height: 50vh;
  transform: rotate(${(p) => p.$visualRotation}deg);
  transition: transform 0.2s ease;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const RotateGroup = styled.div`
  display: flex;
  gap: 0.5rem;
`;

type Props = {
  file: File;
  onConfirm: (page: number, rotation: number) => Promise<void>;
  onClose: () => void;
};

export function PdfPagePicker({ file, onConfirm, onClose }: Props) {
  const intl = useIntl();
  const [pdf, setPdf] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [thumbnails, setThumbnails] = useState<(string | null)[]>([]);
  const [selectedPage, setSelectedPage] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [visualRotation, setVisualRotation] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [step, setStep] = useState<'page' | 'rotate'>('page');
  const [previewDataUrl, setPreviewDataUrl] = useState<string | null>(null);

  const renderPreview = async (
    doc: pdfjsLib.PDFDocumentProxy,
    pageNum: number
  ) => {
    const page = await doc.getPage(pageNum);
    const viewport = page.getViewport({ scale: PREVIEW_SCALE });
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: ctx, viewport }).promise;
    setPreviewDataUrl(canvas.toDataURL());
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      configurePdfjsWorker();

      const arrayBuffer = await file.arrayBuffer();
      if (cancelled) return;

      const loaded = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      if (cancelled) return;

      setPdf(loaded);

      if (loaded.numPages === 1) {
        setStep('rotate');
        await renderPreview(loaded, 1);
        return;
      }

      setThumbnails(new Array(loaded.numPages).fill(null));

      for (let i = 1; i <= loaded.numPages; i++) {
        if (cancelled) break;
        const page = await loaded.getPage(i);
        const viewport = page.getViewport({ scale: THUMBNAIL_SCALE });
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d')!;
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        await page.render({ canvasContext: ctx, viewport }).promise;
        if (!cancelled) {
          const dataUrl = canvas.toDataURL();
          setThumbnails((prev) => {
            const next = [...prev];
            next[i - 1] = dataUrl;
            return next;
          });
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [file]);

  const handlePageSelect = async (pageNum: number) => {
    if (!pdf) return;
    setSelectedPage(pageNum);
    setRotation(0);
    setVisualRotation(0);
    setPreviewDataUrl(null);
    setStep('rotate');
    await renderPreview(pdf, pageNum);
  };

  const rotateLeft = () => {
    setVisualRotation((r) => r - 90);
    setRotation((r) => (r - 90 + 360) % 360);
  };
  const rotateRight = () => {
    setVisualRotation((r) => r + 90);
    setRotation((r) => (r + 90) % 360);
  };

  const handleConfirm = async () => {
    setIsUploading(true);
    try {
      await onConfirm(selectedPage, rotation);
    } catch {
      // error handling is the caller's responsibility
    } finally {
      setIsUploading(false);
      onClose();
    }
  };

  const handleBack = () => {
    setStep('page');
    setPreviewDataUrl(null);
    setRotation(0);
    setVisualRotation(0);
  };

  if (step === 'page') {
    return (
      <Container>
        <Title>{intl.formatMessage({ id: 'pdfPagePicker.title' })}</Title>
        <Grid>
          {thumbnails.map((src, i) => (
            <ThumbnailWrapper key={i} onClick={() => handlePageSelect(i + 1)}>
              {src ? (
                <ThumbnailImage src={src} alt={`Page ${i + 1}`} />
              ) : (
                <ThumbnailPlaceholder />
              )}
              <PageLabel>
                {intl.formatMessage(
                  { id: 'pdfPagePicker.page' },
                  { page: i + 1 }
                )}
              </PageLabel>
            </ThumbnailWrapper>
          ))}
        </Grid>
      </Container>
    );
  }

  return (
    <Container>
      <PreviewWrapper>
        {previewDataUrl ? (
          <PreviewImage
            src={previewDataUrl}
            alt={`Page ${selectedPage}`}
            $visualRotation={visualRotation}
          />
        ) : (
          <ThumbnailPlaceholder style={{ width: '200px', height: '280px' }} />
        )}
      </PreviewWrapper>
      <Controls>
        <RotateGroup>
          {pdf && pdf.numPages > 1 && (
            <Button type={ButtonType.Secondary} onClick={handleBack}>
              {intl.formatMessage({ id: 'pdfPagePicker.back' })}
            </Button>
          )}
          <Button type={ButtonType.Secondary} onClick={rotateLeft}>
            {intl.formatMessage({ id: 'pdfPagePicker.rotateLeft' })}
          </Button>
          <Button type={ButtonType.Secondary} onClick={rotateRight}>
            {intl.formatMessage({ id: 'pdfPagePicker.rotateRight' })}
          </Button>
        </RotateGroup>
        <Button
          onClick={handleConfirm}
          disabled={!pdf}
          loading={isUploading}
          loadingText={intl.formatMessage({ id: 'pdfPagePicker.importing' })}
        >
          {intl.formatMessage({ id: 'pdfPagePicker.import' })}
        </Button>
      </Controls>
    </Container>
  );
}
